import { FormEditorTypes } from "@/components/forms/form-editor.config.ts";
import { FormEditor } from "@/components/forms/FormEditor.tsx";
import { Search } from "@/components/search/Search.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import { usePerformanceReviewEditPage } from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { usePerformanceReviewTemplateSearch } from "@/queries/performance-reviews/use-performance-review-template.query.ts";
import {
  usePerformanceReviewInvalidateSetup,
  usePerformanceReviewReviewerTypeUpdateForm,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiFormTemplateFilter } from "@/types/forms/form-template.types.ts";
import {
  ApiPerformanceReviewEdit,
  ApiPerformanceReviewReviewerTypeEdit,
} from "@/types/performance-review.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  FullScreenPage,
  Radio,
  Skeleton,
  Stack,
  Switch,
  Title,
} from "@skailer-com/uikit";
import { round } from "lodash";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

export interface PerformanceReviewEditReviewerTypeFormPageProps {
  review: ApiPerformanceReviewEdit;
  reviewerType: ApiPerformanceReviewReviewerTypeEdit;
}

export const PerformanceReviewEditReviewerTypeFormPage = ({
  review,
  reviewerType,
}: PerformanceReviewEditReviewerTypeFormPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = usePerformanceReviewEditPage();

  const { canIView } = usePermissions();

  const reviewerTypeUpdate = usePerformanceReviewReviewerTypeUpdateForm();
  const invalidateSetup = usePerformanceReviewInvalidateSetup();

  const shouldShowInitialScreen = !reviewerType.form;
  const [isCustomForm, setIsCustomForm] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);

  const [templateSearchKey, setTemplateSearchKey] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<ApiFormTemplateFilter>({
    page_number: 1,
    text: undefined,
  });

  const [syncedFormId, setSyncedFormId] = useState(reviewerType.form?.id);
  if (reviewerType.form?.id !== syncedFormId) {
    setSyncedFormId(reviewerType.form?.id);
    setIsCustomForm(false);
  }

  useDebounce(
    () => {
      setFilter({ ...filter, text: templateSearchKey });
    },
    300,
    [templateSearchKey]
  );

  const { data: templates, isFetching } = usePerformanceReviewTemplateSearch(
    filter,
    canIView(ApiPermission.performance_review_templates)
  );

  const onSubmit = async () => {
    if (!reviewerType.id) return;
    if (!shouldShowInitialScreen) {
      invalidateSetup();
      navigateToStep(review.id, PerformanceReviewEditPageStep.forms);
      return;
    } else if (isCustomForm) {
      try {
        await reviewerTypeUpdate.mutateAsync({
          id: review.id,
          reviewer_type_id: reviewerType.id,
          payload: {
            create_new_form: true,
          },
        });
      } catch {
        return;
      }
    } else if (selectedTemplateId) {
      try {
        await reviewerTypeUpdate.mutateAsync({
          id: review.id,
          reviewer_type_id: reviewerType.id,
          payload: {
            form_template_id: selectedTemplateId,
          },
        });
        navigateToStep(review.id, PerformanceReviewEditPageStep.forms);
      } catch {
        return;
      }
    }
  };

  const renderContent = () => {
    if (shouldShowInitialScreen) {
      return (
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title
              header={t("performance_review:select_template_title")}
              caption={t("performance_review:select_template_description")}
              size={"lg"}
            />
            <Switch
              checked={isCustomForm}
              onChange={() => {
                setIsCustomForm(!isCustomForm);
              }}
              title={t("performance_review:start_from_scratch_title")}
              description={t("performance_review:start_from_scratch_description")}
            />
            {!isCustomForm && (
              <>
                <Box>
                  <Search
                    placeholder={t("common:search")}
                    value={templateSearchKey}
                    onInput={(e) => {
                      setTemplateSearchKey(e.currentTarget.value);
                    }}
                    type={"text"}
                  />
                </Box>
                {isFetching && <Skeleton className={"w-full h-10"} />}
                {templates && templates.items.length > 0 && !isFetching && (
                  <Stack gap={"sm"}>
                    {templates.items.map((template) => (
                      <Fragment key={`template-${template.id}`}>
                        <Radio
                          title={template.title}
                          checked={selectedTemplateId === template.id}
                          onChange={() => {
                            setSelectedTemplateId(template.id);
                          }}
                          caption={`${t("plurals:question_count", { count: template.total_questions })} · ${t(
                            "plurals:plural_minutes",
                            {
                              count: round(template.total_estimated_time_seconds / 60),
                            }
                          )}`}
                        />
                        <hr className={"ml-8"} />
                      </Fragment>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>

          <Button size={"lg"} isLoading={reviewerTypeUpdate.isPending} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack gap={"lg"} justify={"center"}>
        {reviewerType.form && reviewerType.id && (
          <FormEditor type={FormEditorTypes.performanceReview} id={reviewerType.id} />
        )}
      </Stack>
    );
  };

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(review.id, PerformanceReviewEditPageStep.forms);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={
        <PerformanceReviewTitleSlot step={PerformanceReviewEditPageStep.forms} review={review} />
      }
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar step={PerformanceReviewEditPageStep.forms} review={review} />
      }
      contentSize={shouldShowInitialScreen ? "sm" : "md"}
    >
      {renderContent()}
    </FullScreenPage>
  );
};
